import {runInAction} from "mobx";
import {bind} from "Mri.Web.Common";
import {AccountStore} from "../../../Account/AccountStore";
import {AppStore} from "../../../Core/AppStore";
import React from "react";

export class PinnedColumnManager {
    constructor(
        private accountStore: AccountStore,
        private appStore: AppStore,
        private ghostLineRef: React.RefObject<HTMLDivElement>,
        private leftResizerRef: React.RefObject<HTMLDivElement>,
        private rightResizerRef: React.RefObject<HTMLDivElement>
    ) {}

    @bind
    public applyMaxPinnedColumnContainer() {
        if (this.accountStore.storage.gridLeftPinnedContainerScrollable) {
            const leftPinnedColumns = this.appStore.gridRef?.api.getAllDisplayedColumns()
                .filter(col => col.getPinned() === 'left');
            if (leftPinnedColumns && leftPinnedColumns.length >= 1) {
                this.enforceLeftPinnedColumnMaxWidth();
                this.syncLeftPinnedColumnScrollWithBottom();
            } else {
                this.removeLeftPinnedColumnScrollEffects();
            }
        }

        if (this.accountStore.storage.gridRightPinnedContainerScrollable) {
            const rightPinnedColumns = this.appStore.gridRef?.api.getAllDisplayedColumns()
                .filter(col => col.getPinned() === 'right');
            if (rightPinnedColumns && rightPinnedColumns.length >= 1) {
                this.enforceRightPinnedColumnMaxWidth();
                this.syncRightPinnedColumnScrollWithBottom();
            } else {
                this.removeRightPinnedColumnScrollEffects();
            }
        }

        this.positionResizers()
    }


    @bind
    public startLeftResize(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) {
        const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const gridRoot = document.querySelector('.ag-root') as HTMLElement;
        const initialWidth = gridRoot.clientWidth * this.accountStore.storage.gridLeftPinnedContainerWidth;
        let newRatio = this.accountStore.storage.gridRightPinnedContainerWidth;

        const onMove = (moveEvent: MouseEvent | TouchEvent) => {
            const clientX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : (moveEvent as MouseEvent).clientX;
            const delta = clientX - startX;
            const newWidth = initialWidth + delta;
            let maxRatio = 0.9;
            if (this.accountStore.storage.gridRightPinnedContainerScrollable) {
                const rightRatio = this.accountStore.storage.gridRightPinnedContainerWidth;
                maxRatio = 1 - (rightRatio + 0.1);
            }
            newRatio = Math.min(Math.max(newWidth / gridRoot.clientWidth, 0.05), maxRatio);
            newRatio = Math.round(newRatio * 100) / 100;

            if (this.ghostLineRef.current) {
                this.ghostLineRef.current.style.display = 'block';
                this.ghostLineRef.current.style.right = `${gridRoot.clientWidth * (1 - newRatio)}px`;
                this.ghostLineRef.current.style.left = "unset";
            }
        };

        const onEnd = () => {
            document.body.style.cursor = 'default';
            if (this.leftResizerRef.current) {
                this.leftResizerRef.current.style.pointerEvents = 'auto';
            }
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);

            runInAction(() => {
                this.accountStore.storage.gridLeftPinnedContainerWidth = newRatio;
                this.applyMaxPinnedColumnContainer();
                this.positionResizers();
                if (this.ghostLineRef.current) {
                    this.ghostLineRef.current.style.display = 'none';
                }
            });
        };

        document.body.style.cursor = 'ew-resize';
        if (this.leftResizerRef.current) {
            this.leftResizerRef.current.style.pointerEvents = 'none';
        }

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onEnd);
    }

    @bind
    public startRightResize(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) {
        const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const gridRoot = document.querySelector('.ag-root') as HTMLElement;
        const initialWidth = gridRoot.clientWidth * this.accountStore.storage.gridRightPinnedContainerWidth;
        let newRatio = this.accountStore.storage.gridRightPinnedContainerWidth;

        const onMove = (moveEvent: MouseEvent | TouchEvent) => {
            const clientX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : (moveEvent as MouseEvent).clientX;
            const delta = startX - clientX;
            const newWidth = initialWidth + delta;
            let maxRatio = 0.9;
            if (this.accountStore.storage.gridLeftPinnedContainerScrollable) {
                const leftRatio = this.accountStore.storage.gridLeftPinnedContainerWidth;
                maxRatio = 1 - (leftRatio + 0.1);
            }

            newRatio = Math.min(Math.max(newWidth / gridRoot.clientWidth, 0.05), maxRatio);
            newRatio = Math.round(newRatio * 100) / 100;

            if (this.ghostLineRef.current) {
                this.ghostLineRef.current.style.display = 'block';
                this.ghostLineRef.current.style.left = `${gridRoot.clientWidth * (1 - newRatio)}px`;
            }
        };

        const onEnd = () => {
            document.body.style.cursor = 'default';
            if (this.rightResizerRef.current) {
                this.rightResizerRef.current.style.pointerEvents = 'auto';
            }
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);

            runInAction(() => {
                this.accountStore.storage.gridRightPinnedContainerWidth = newRatio;
                this.applyMaxPinnedColumnContainer();
                this.positionResizers();
                if (this.ghostLineRef.current) {
                    this.ghostLineRef.current.style.display = 'none';
                }
            });
        };

        document.body.style.cursor = 'ew-resize';
        if (this.rightResizerRef.current) {
            this.rightResizerRef.current.style.pointerEvents = 'none';
        }

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onEnd);
    }

    @bind
    public positionResizers() {
        const leftPinned = document.querySelector('.ag-pinned-left-cols-container') as HTMLElement;
        const rightPinned = document.querySelector('.ag-pinned-right-cols-container') as HTMLElement;
        const gridRoot = document.querySelector('.ag-root') as HTMLElement;

        if (leftPinned && this.leftResizerRef.current) {
            const leftRect = leftPinned.getBoundingClientRect();
            const gridRect = gridRoot.getBoundingClientRect();
            this.leftResizerRef.current.style.left = `${leftRect.right - gridRect.left}px`;
        }

        if (rightPinned && this.rightResizerRef.current) {
            const rightRect = rightPinned.getBoundingClientRect();
            const gridRect = gridRoot.getBoundingClientRect();
            this.rightResizerRef.current.style.left = `${rightRect.left - gridRect.left - 5}px`; // 5px = resizer width
        }
    }

    @bind
    private enforceLeftPinnedColumnMaxWidth() {
        const gridRoot = document.querySelector('.ag-root') as HTMLElement;
        const pinnedContainer = document.querySelector('.ag-pinned-left-cols-container') as HTMLElement;
        const pinnedHeader = document.querySelector('.ag-pinned-left-header') as HTMLElement;
        const horizontalLeftSpacer = document.querySelector('.ag-horizontal-left-spacer') as HTMLElement;
        const mainHorizontalScrollContainer = document.querySelector('.ag-body-horizontal-scroll') as HTMLElement;
        if (!gridRoot || !pinnedContainer || !pinnedHeader || !horizontalLeftSpacer || !mainHorizontalScrollContainer) return;

        const maxWidth = gridRoot.clientWidth * this.accountStore.storage.gridLeftPinnedContainerWidth;
        const minWidth = gridRoot.clientWidth * this.accountStore.storage.gridLeftPinnedContainerWidth;

        const className = 'left-pinned-column-max-width';
        const styleId = `style-${className}`;

        const isMobile = /Mobi|Android|iPhone|iPad|iPod|Touch/.test(navigator.userAgent);

        const existingStyle = document.getElementById(styleId);
        if (existingStyle) existingStyle.remove();

        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
    .${className} {
      overflow-x: auto;
      overflow-y: hidden;
      width: auto !important;
      max-width: ${maxWidth}px !important;
      min-width: ${minWidth}px !important;
      pointer-events: auto !important;
    }
  `;

        document.head.appendChild(style);

        pinnedContainer.classList.add(className);
        pinnedHeader.classList.add(className);
        horizontalLeftSpacer.classList.add(className);
        horizontalLeftSpacer.classList.add('p-1');

        // Apply height styles and remove ag-hidden from mainHorizontalScrollContainer
        mainHorizontalScrollContainer.style.maxHeight = '15px';
        mainHorizontalScrollContainer.style.minHeight = '15px';
        mainHorizontalScrollContainer.style.height = '15px';
        mainHorizontalScrollContainer.classList.remove('ag-hidden');

        // Enable touch scrolling on mobile
        pinnedContainer.classList.add('container-touch-scrollable');
        pinnedHeader.classList.add('touch-scrollable-x-hidden');
        horizontalLeftSpacer.classList.add('touch-scrollable-mobile');
    }

    @bind
    private syncLeftPinnedColumnScrollWithBottom() {
        const pinnedCols = document.querySelector('.ag-pinned-left-cols-container') as HTMLElement;
        const pinnedHeader = document.querySelector('.ag-pinned-left-header') as HTMLElement;
        const horizontalLeftSpacer = document.querySelector('.ag-horizontal-left-spacer') as HTMLElement;
        if (!pinnedCols || !pinnedHeader || !horizontalLeftSpacer) return;

        pinnedCols.style.overflowX = 'hidden';
        pinnedHeader.style.overflowX = 'hidden';
        pinnedCols.style.setProperty('border-right', '2px solid #1818187d', 'important');
        pinnedHeader.style.setProperty('border-right', '2px solid #1818187d', 'important');
        // Delete element if exists.
        const existingScroll = horizontalLeftSpacer.querySelector('.left-pinned-scroll-container');

        // Create child for pinned scroll
        const pinnedScroll = document.createElement('div');
        pinnedScroll.classList.add('ag-body-horizontal-scroll-container');
        pinnedScroll.classList.add('left-pinned-scroll-container');
        pinnedScroll.style.width = `${pinnedCols.scrollWidth - 7}px`;
        pinnedScroll.style.height = '1px'; // Minimal height to make it visible
        pinnedScroll.style.background = 'transparent'; // Optional
        horizontalLeftSpacer.style.overflowX = 'auto'; // Enable horizontal scrolling
        horizontalLeftSpacer.appendChild(pinnedScroll);
        
        // Sync scroll from spacer to header and cols
        pinnedHeader.scrollLeft = pinnedCols.scrollLeft;
        const syncScroll = (scrollLeft: number) => {
            pinnedCols.scrollLeft = scrollLeft;
            pinnedHeader.scrollLeft = scrollLeft;
        };
        const syncTouchScroll = (scrollLeft: number) => {
            horizontalLeftSpacer.scrollLeft = scrollLeft;
        };

        horizontalLeftSpacer.addEventListener('scroll', () => {
            syncScroll(horizontalLeftSpacer.scrollLeft);
        });

        pinnedCols.addEventListener('scroll', () => {
            syncScroll(pinnedCols.scrollLeft);
            syncTouchScroll(pinnedCols.scrollLeft);
        });

        if (existingScroll) {
            horizontalLeftSpacer.removeChild(existingScroll);
        }
    }

    @bind
    private removeLeftPinnedColumnScrollEffects() {
        const className = 'left-pinned-column-max-width';
        const styleId = `style-${className}`;

        // Remove the style tag
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
            existingStyle.remove();
        }

        // Remove the class from elements
        const elements = [
            document.querySelector('.ag-pinned-left-cols-container'),
            document.querySelector('.ag-pinned-left-header'),
            document.querySelector('.ag-horizontal-left-spacer')
        ];

        elements.forEach(el => {
            if (el) el.classList.remove(className);
        });

        // Remove scroll container if it exists
        const scrollContainer = document.querySelector('.ag-horizontal-left-spacer .ag-body-horizontal-scroll-container');
        if (scrollContainer && scrollContainer.parentElement) {
            scrollContainer.parentElement.removeChild(scrollContainer);
        }
    }

    @bind
    private enforceRightPinnedColumnMaxWidth() {
        const gridRoot = document.querySelector('.ag-root') as HTMLElement;
        const pinnedContainer = document.querySelector('.ag-pinned-right-cols-container') as HTMLElement;
        const pinnedHeader = document.querySelector('.ag-pinned-right-header') as HTMLElement;
        const verticalScrollBar = document.querySelector('.ag-body-vertical-scroll-viewport') as HTMLElement;
        const mainHorizontalScrollContainer = document.querySelector('.ag-body-horizontal-scroll') as HTMLElement;
        const horizontalRightSpacer = document.querySelector('.ag-horizontal-right-spacer') as HTMLElement;
        if (!gridRoot || !pinnedContainer || !pinnedHeader || !horizontalRightSpacer || !mainHorizontalScrollContainer) return;

        const maxWidth = gridRoot.clientWidth * this.accountStore.storage.gridRightPinnedContainerWidth;
        const minWidth = gridRoot.clientWidth * this.accountStore.storage.gridRightPinnedContainerWidth;
        const className = 'right-pinned-column-max-width';
        const styleId = `style-${className}`;

        const existingStyle = document.getElementById(styleId);
        if (existingStyle) existingStyle.remove();

        const isMobile = /Mobi|Android|iPhone|iPad|iPod|Touch/.test(navigator.userAgent);

        const hasVerticalScroll = !isMobile && verticalScrollBar.scrollHeight > 10;
        const paddingStyle = hasVerticalScroll ? 'padding-right: 15px !important; margin-right: 15px !important;' : '';

        const style = document.createElement('style');
        style.id = styleId;

        style.innerHTML = `
          .${className} {
            overflow-x: auto;
            overflow-y: hidden;
            width: auto !important;
            max-width: ${maxWidth}px !important;
            min-width: ${minWidth}px !important;
            pointer-events: auto !important;
            box-sizing: border-box;
          }
          .ag-pinned-right-header.${className} {
            overflow-x: hidden !important;
            ${paddingStyle}
            border-left: 2px solid #1818187d !important;
          }
          .ag-pinned-right-cols-container.${className} {
            padding-right: 1px !important;
            margin-right: 0 !important;
            border-left: 2px solid #1818187d !important;
          }
          .ag-horizontal-right-spacer.${className} {
            ${paddingStyle}
          }
        `;

        document.head.appendChild(style);

        pinnedContainer.classList.add(className);
        pinnedHeader.classList.add(className);
        horizontalRightSpacer.classList.add(className);
        horizontalRightSpacer.classList.add('p-1');

        // Apply height styles and remove ag-hidden from mainHorizontalScrollContainer
        mainHorizontalScrollContainer.style.maxHeight = '15px';
        mainHorizontalScrollContainer.style.minHeight = '15px';
        mainHorizontalScrollContainer.style.height = '15px';
        mainHorizontalScrollContainer.classList.remove('ag-hidden');

        // Enable touch scrolling on mobile
        pinnedContainer.classList.add('container-touch-scrollable');
        pinnedHeader.classList.add('touch-scrollable-x-hidden');
        horizontalRightSpacer.classList.add('touch-scrollable-mobile');
    }

    @bind
    private syncRightPinnedColumnScrollWithBottom() {
        const pinnedCols = document.querySelector('.ag-pinned-right-cols-container') as HTMLElement;
        const pinnedHeader = document.querySelector('.ag-pinned-right-header') as HTMLElement;
        const horizontalRightSpacer = document.querySelector('.ag-horizontal-right-spacer') as HTMLElement;
        if (!pinnedCols || !pinnedHeader || !horizontalRightSpacer) return;

        pinnedCols.style.overflowX = 'hidden';
        pinnedHeader.style.overflowX = 'hidden';
        pinnedCols.style.setProperty('border-left', '2px solid #1818187d', 'important');
        pinnedHeader.style.setProperty('border-left', '2px solid #1818187d', 'important');

        const existingScroll = horizontalRightSpacer.querySelector('.right-pinned-scroll-container');
        const pinnedScroll = document.createElement('div');
        pinnedScroll.classList.add('ag-body-horizontal-scroll-container');
        pinnedScroll.classList.add('right-pinned-scroll-container');
        pinnedScroll.style.width = `${pinnedCols.scrollWidth - 7}px`;
        pinnedScroll.style.height = '1px';
        pinnedScroll.style.background = 'transparent';
        horizontalRightSpacer.style.overflowX = 'auto';
        horizontalRightSpacer.appendChild(pinnedScroll);

        // Sync scroll from spacer to header and cols
        pinnedHeader.scrollLeft = pinnedCols.scrollLeft;
        const syncScroll = (scrollLeft: number) => {
            pinnedCols.scrollLeft = scrollLeft;
            pinnedHeader.scrollLeft = scrollLeft;
        };

        const syncTouchScroll = (scrollLeft: number) => {
            horizontalRightSpacer.scrollLeft = scrollLeft;
        };

        horizontalRightSpacer.addEventListener('scroll', () => {
            syncScroll(horizontalRightSpacer.scrollLeft);
        });

        pinnedCols.addEventListener('scroll', () => {
            syncScroll(pinnedCols.scrollLeft);
            syncTouchScroll(pinnedCols.scrollLeft);
        });

        if (existingScroll) {
            horizontalRightSpacer.removeChild(existingScroll);
        }
    }

    @bind
    private removeRightPinnedColumnScrollEffects() {
        const className = 'right-pinned-column-max-width';
        const styleId = `style-${className}`;

        // Remove the style tag
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
            existingStyle.remove();
        }

        // Remove the class from elements
        const elements = [
            document.querySelector('.ag-pinned-right-cols-container'),
            document.querySelector('.ag-pinned-right-header'),
            document.querySelector('.ag-horizontal-right-spacer')
        ];

        elements.forEach(el => {
            if (el) el.classList.remove(className);
        });

        // Remove scroll container if it exists
        const scrollContainer = document.querySelector('.ag-horizontal-right-spacer .ag-body-horizontal-scroll-container');
        if (scrollContainer && scrollContainer.parentElement) {
            scrollContainer.parentElement.removeChild(scrollContainer);
        }
    }
}
